import { create } from "zustand";

import type { HelpScreen } from "./topics";

/** Aide en ligne : centre d'aide ouvert (écran, onglet, fiche à montrer) et prise en main. */

export type HelpTab = "screen" | "all" | "glossary";
/** Suite donnée à la prise en main depuis l'accueil : créer un dossier, ou ouvrir le dossier d'exemple. */
export type OnboardingIntent = "create" | "example";

const ONBOARDING_KEY = "generex:prise-en-main";

interface HelpStore {
  open: boolean;
  /** Écran de la vue qui a ouvert l'aide ; null : vue à venir (pas encore de fiche). */
  screen: HelpScreen | null;
  tab: HelpTab;
  /** Fiche à déplier et à montrer. */
  topic: string | null;
  onboarding: boolean;
  /** Demande laissée par la prise en main, que l'accueil exécute (avec son fondu de sortie). */
  intent: OnboardingIntent | null;
  openHelp: (options?: { screen?: HelpScreen | null; topic?: string; tab?: HelpTab }) => void;
  closeHelp: () => void;
  openOnboarding: () => void;
  closeOnboarding: (intent?: OnboardingIntent) => void;
  clearIntent: () => void;
}

export const useHelp = create<HelpStore>((set) => ({
  open: false,
  screen: null,
  tab: "screen",
  topic: null,
  onboarding: false,
  intent: null,
  openHelp: (options = {}) =>
    set(() => {
      const screen = options.screen ?? null;
      return {
        open: true,
        screen,
        topic: options.topic ?? null,
        tab: options.tab ?? (screen ? "screen" : "all"),
      };
    }),
  closeHelp: () => set({ open: false, topic: null }),
  openOnboarding: () => set({ onboarding: true, open: false }),
  closeOnboarding: (intent) => {
    try {
      localStorage.setItem(ONBOARDING_KEY, "1");
    } catch {
      /* stockage bloqué : la prise en main se rouvrira */
    }
    set({ onboarding: false, intent: intent ?? null });
  },
  clearIntent: () => set({ intent: null }),
}));

/** Première visite sur ce poste : la prise en main s'ouvre d'elle-même (une seule fois). */
export function onboardingSeen(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === "1";
  } catch {
    return true;
  }
}
